import {
  EMPTY_METRIC_VALUE,
  isEmptyMetricValue,
  SERVICE_HEALTH_RED_METRIC_IDS,
  type ServiceHealthMetricId,
  type SloMetric,
  type SloMetricStatus,
} from './sloHealthMetrics';

export type ServiceHealthEmptyReason =
  | 'agent_missing'
  | 'enable_reliability_metrics'
  | 'no_data'
  | null;

export type DeriveServiceHealthEmptyReasonArgs = {
  showAgentInstallBanner: boolean;
  /** `undefined` when capabilities have not loaded yet. */
  reliabilityMetricsDataAvailable?: boolean;
  metrics: Array<Pick<SloMetric, 'id' | 'value'>>;
};

export const isServiceHealthRedMetric = (id: string): boolean =>
  SERVICE_HEALTH_RED_METRIC_IDS.has(id as ServiceHealthMetricId);

type LockableServiceHealthMetric = {
  id: string;
  value: string;
  unit?: string;
  status: SloMetricStatus;
  infoTooltip?: string;
};

/** Lock agent/collector-dependent tiles (value `--`, status `locked`). */
export const lockServiceHealthRedMetrics = <
  T extends LockableServiceHealthMetric
>(
  metrics: T[]
): T[] =>
  metrics.map((metric) =>
    isServiceHealthRedMetric(metric.id)
      ? {
          ...metric,
          value: EMPTY_METRIC_VALUE,
          unit: undefined,
          status: 'locked' as const,
          infoTooltip: undefined,
        }
      : metric
  );

/**
 * Prefer actionable CTAs only when they explain the empty tiles.
 * - Agent missing → install agent (Figma).
 * - Collector missing + RED empty, but some infra data present → enable collector.
 * - Everything `--` → plain no-data (do not blame collector when node availability is also missing).
 */
export const deriveServiceHealthEmptyReason = ({
  showAgentInstallBanner,
  reliabilityMetricsDataAvailable,
  metrics,
}: DeriveServiceHealthEmptyReasonArgs): ServiceHealthEmptyReason => {
  if (showAgentInstallBanner) {
    return 'agent_missing';
  }

  const allEmpty = metrics.every((metric) => isEmptyMetricValue(metric.value));
  const allRedEmpty = metrics
    .filter((metric) => isServiceHealthRedMetric(metric.id))
    .every((metric) => isEmptyMetricValue(metric.value));
  const hasNonRedValue = metrics.some(
    (metric) =>
      !isServiceHealthRedMetric(metric.id) && !isEmptyMetricValue(metric.value)
  );

  if (
    reliabilityMetricsDataAvailable === false &&
    allRedEmpty &&
    hasNonRedValue
  ) {
    return 'enable_reliability_metrics';
  }

  if (allEmpty) {
    return 'no_data';
  }

  return null;
};
